/**
 * Reminders on one case, and a way to set one for yourself (Hannah, 20 Oct 2026: "have you figured
 * out a way for us to set reminders for ourselves in the software where I can remind myself to check
 * in?").
 *
 * A reminder set here waits until its date, then shows in the setter's "Action needed" list on the
 * Alerts page until it is marked done. This section lists everyone's, so whoever covers the case can
 * see what is already set.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CaseReminderApi, { CaseReminderDto, STAFF_REMINDER } from '../../api/CaseReminderApi';
import { formatDateOnly } from '../../utils/formatDateOnly';

interface CaseReminderSectionProps {
  Ticket_guid: string;
}

/** Today as YYYY-MM-DD in the browser's own calendar, for the date input's lower bound. */
function todayDateOnly(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

const CaseReminderSection: React.FC<CaseReminderSectionProps> = ({ Ticket_guid }) => {
  const [reminders, setReminders] = useState<CaseReminderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await CaseReminderApi.listForTicket(Ticket_guid);
      setReminders(
        all.filter((r) => r.CaseReminder_State === 'pending' || r.CaseReminder_State === 'open'),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminders for this case');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const openDialog = () => {
    setDueDate('');
    setNote('');
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await CaseReminderApi.create(Ticket_guid, { note, dueDate });
      setDialogOpen(false);
      await fetchReminders();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to set the reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (reminder: CaseReminderDto) => {
    setResolving(reminder.CaseReminder_guid);
    setError(null);
    try {
      await CaseReminderApi.resolve(reminder.CaseReminder_guid);
      setReminders((current) =>
        current.filter((r) => r.CaseReminder_guid !== reminder.CaseReminder_guid),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the reminder');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Reminders
          </Typography>
          <Button size="small" variant="outlined" onClick={openDialog}>
            Set reminder
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : reminders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No reminders on this case. Set one to have it come back to you on a date.
          </Typography>
        ) : (
          reminders.map((reminder, index) => (
            <Box key={reminder.CaseReminder_guid}>
              {index > 0 && <Divider sx={{ my: 1 }} />}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {/* The day that was picked. Stored as midnight on a UTC server, so parsing
                          it as an instant shows the day before anywhere in the US. */}
                      {formatDateOnly(reminder.CaseReminder_DueAt)}
                    </Typography>
                    <Chip
                      label={reminder.CaseReminder_State === 'open' ? 'due' : 'waiting'}
                      size="small"
                      color={reminder.CaseReminder_State === 'open' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    {reminder.CaseReminder_CreatedByName && (
                      <Typography variant="caption" color="text.secondary">
                        Set by {reminder.CaseReminder_CreatedByName}
                      </Typography>
                    )}
                  </Stack>
                  {/* Plain text: the note is shown exactly as typed, line breaks kept. */}
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reminder.CaseReminder_Type === STAFF_REMINDER
                      ? reminder.CaseReminder_Note
                      : reminder.title}
                  </Typography>
                </Box>
                {reminder.CaseReminder_Type === STAFF_REMINDER && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={resolving === reminder.CaseReminder_guid}
                    onClick={() => handleResolve(reminder)}
                  >
                    {resolving === reminder.CaseReminder_guid ? 'Saving…' : 'Mark done'}
                  </Button>
                )}
              </Stack>
            </Box>
          ))
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Set a reminder</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              It shows in your Action needed list on the Alerts page on this date, and stays there
              until you mark it done.
            </Typography>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <TextField
              label="Remind me on"
              type="date"
              size="small"
              fullWidth
              required
              disabled={saving}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayDateOnly() }}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <TextField
              label="Note"
              size="small"
              fullWidth
              required
              multiline
              minRows={3}
              disabled={saving}
              helperText="What to check, so it makes sense when it comes back"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !dueDate || !note.trim()}
          >
            {saving ? 'Saving…' : 'Set reminder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CaseReminderSection;
